import { Fragment, useEffect, useState } from 'react';
import { Popover, Transition } from '@headlessui/react';

import { FaCaretDown } from 'react-icons/fa';
import { CgProfile } from 'react-icons/cg';
import { FaBars } from 'react-icons/fa';
import { FaTimes } from 'react-icons/fa';
import { IconContext } from 'react-icons';
import Link from 'next/link';
import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import { TeamMemberType, Edition } from '../../../utils/enums';
import { useUser } from '@auth0/nextjs-auth0';
import { getUserRole } from '../../../utils/helpers/userHelper';
import useSWR from 'swr';
import { UserInfo } from '../../../utils/interfaces';
import * as Sentry from '@sentry/nextjs';
import getConfirmedClassicURL from '../../../utils/helpers/getConfirmedClassicURL';

const routeClassic = (location: string) => {
  const Classic_URL = getConfirmedClassicURL(window?.location.host);
  return Classic_URL + '/' + location;
};
const loggedOut = [
  {
    name: 'Solutions',
    href: '/solutions',
  },
  {
    name: 'About',
    href: '/about',
  },
  {
    name: 'Pricing',
    href: '/pricing',
  },
  {
    name: 'Log In',
    href: '/api/auth/login',
  },
  {
    name: 'Sign Up',
    href: '/api/auth/login?logintype=create&redirect-url=/signup/completeSignup',
  },
];

const appPages = [
  {
    name: 'My Meetings',
    href: '/meetings',
  },
  {
    name: 'Scheduling',
    href: '/scheduler',
  },
  {
    name: 'Meet-&-Greet',
    href: '/connection',
  },
  {
    name: 'Encounters',
    href: '/encounters',
  },
];

const ALLOWED_ENCOUNTERS_EDITIONS = [
  Edition.SuperConnector,
  Edition.Premium,
  Edition.Team,
  Edition.Personal,
  Edition.Participant,
  Edition.FreeTrial,
  Edition.Professional,
  Edition.MeetAndGreet,
];

const classicSubmenu = [
  {
    name: 'Templates and Integrations',
    href: '/redirect?redirect=/user/emailOptions&classic=true',
  },
  { name: 'Custom Meeting Requests', href: '/custommeeting' },
];

const teamAdminSubmenu = [
  { name: 'Manage Users', href: '/team/users' },
  { name: 'Team Schedulers', href: '/team/schedulers' },
  { name: 'Team Metrics', href: '/metrics' },
];

const userDropDown = [
  {
    name: 'Host a Meet-&-Greet',
    href: '/event/hostdashboard',
    description: '(Try it now)',
  },
  {
    name: 'Settings',
    href: '/user/profile',
  },
  // {
  //   name: 'Advanced Settings',
  //   href: '/redirect?redirect=/user/emailOptions&classic=true',
  //   description: '(May require login)',
  // },
  // {
  //   name: 'Settings',
  //   href: '/user/profile',
  // },
  // {
  //   name: 'Locations',
  //   href: '/user/locations',
  // },
  // {
  //   name: 'Templates',
  //   href: '/user/templates',
  // },
  // {
  //   name: 'Host Events',
  //   href: '/event/dashbaord',
  // },
  // {
  //   name: 'Networking',
  //   href: '/connection',
  // },
  {
    name: 'Classic',
    submenu: classicSubmenu,
    description: '(Requires login)',
  },
  {
    name: 'Help',
    href: '/redirect?redirect=https%3A%2F%2Fconfirmedapp.atlassian.net%2Fwiki%2Fspaces%2FCIS%2Foverview&classic=false',
  },
  // {
  //   name: 'Contact Us',
  //   href: '/api/auth/logout',
  // },
  {
    name: 'Logout',
    href: '/api/auth/logout',
  },
];

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

type Props = {
  hostName: string;
};

const Header: NextPage<Props> = ({ hostName }) => {
  const { user } = useUser();
  const userInfo = user as UserInfo;
  const router = useRouter();
  const { data: adminAccountDetails } = useSWR(
    userInfo?.accountDetails?.status === 500 && userInfo?.sub
      ? `/api/admin/accountdetails/${userInfo.sub}`
      : null
  );

  const { data: adminPreferences } = useSWR(
    userInfo?.userPreferences?.status === 500 && userInfo?.sub
      ? `/api/admin/preferences/${userInfo.sub}`
      : null
  );

  const accountDetails = adminAccountDetails || userInfo?.accountDetails;
  const userPreferences = adminPreferences || userInfo?.userPreferences;

  const userRole = getUserRole(user);

  const isTeamAdmin =
    accountDetails?.teamMemberType === TeamMemberType.administrator ||
    accountDetails?.teamMemberType === TeamMemberType.owner;

  function renderHeader() {
    if (userRole === 'guest') {
      return (
        <div className="hidden md:flex items-center justify-end md:space-x-4 lg:space-x-10">
          {loggedOut.map((item) => (
            <Link key={item.name} href={item.href}>
              <a className="text-sm lg:text-base font-medium text-gray-500 hover:text-gray-900 whitespace-nowrap">
                {item.name}
              </a>
            </Link>
          ))}
        </div>
      );
    } else if (userRole === 'editor' || userRole === 'admin') {
      const filteredPages = appPages.filter((page) => {
        if (page.name === 'Encounters') {
          return ALLOWED_ENCOUNTERS_EDITIONS.includes(
            accountDetails?.edition as Edition
          );
        }
        return true;
      });

      return (
        <div className="hidden md:flex items-center justify-end md:space-x-4 lg:space-x-10">
          {filteredPages.map((item) => (
            <Link key={item.name} href={item.href}>
              <a
                className={classNames(
                  'text-sm lg:text-base font-medium hover:text-gray-900 whitespace-nowrap',
                  router.pathname === item.href
                    ? 'text-confirmed-secondary'
                    : 'text-gray-500'
                )}
              >
                {item.name}
              </a>
            </Link>
          ))}
        </div>
      );
    }
    return null;
  }

  function renderDropdown() {
    const [showClassicSubmenu, setShowClassicSubmenu] = useState(false);
    const [showTeamAdminSubmenu, setShowTeamAdminSubmenu] = useState(false);

    if (userRole === 'editor' || userRole === 'admin') {
      return (
        <Popover className="items-center">
          {({ open }) => (
            <>
              <Popover.Button
                className={classNames(
                  open ? 'text-gray-900' : 'text-gray-500',
                  'group bg-white rounded-md inline-flex items-center text-base font-medium hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ml-4'
                )}
              >
                <IconContext.Provider value={{ size: '3em' }}>
                  <div>
                    <CgProfile className="inline" />
                  </div>
                </IconContext.Provider>

                <span className="pl-2">
                  {userPreferences?.firstName ||
                    userInfo?.userPreferences?.firstName ||
                    ''}{' '}
                  {userPreferences?.lastName ||
                    userInfo?.userPreferences?.lastName ||
                    ''}
                </span>
                <FaCaretDown
                  className={classNames(
                    open ? 'text-gray-600' : 'text-gray-400',
                    'ml-2 h-5 w-5 group-hover:text-gray-500'
                  )}
                  aria-hidden="true"
                />
              </Popover.Button>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1"
              >
                <Popover.Panel className="absolute z-10 max-w-md px-2 mt-3 sm:px-0 right-1">
                  <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                    <div className="relative bg-white sm:p-6 p-4">
                      <div className="space-y-2">
                        {/* Team Admin */}
                        {isTeamAdmin && (
                          <div className="relative">
                            <button
                              className="flex items-center justify-between w-full p-2 rounded-lg text-base text-gray-900 hover:bg-gray-50"
                              onClick={() =>
                                setShowTeamAdminSubmenu(!showTeamAdminSubmenu)
                              }
                            >
                              <span>Team Admin</span>
                              <FaCaretDown
                                className={classNames(
                                  showTeamAdminSubmenu ? 'rotate-180' : '',
                                  'h-4 w-4 transition-transform'
                                )}
                              />
                            </button>
                            {showTeamAdminSubmenu && (
                              <div className="ml-4 mt-1 space-y-1">
                                {teamAdminSubmenu.map((subItem) => (
                                  <Link key={subItem.name} href={subItem.href}>
                                    <a className="block p-2 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded">
                                      {subItem.name}
                                    </a>
                                  </Link>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Superadmin */}
                        {userRole === 'admin' && (
                          <Link href="/superadmin">
                            <a className="block p-2 rounded-lg text-base text-gray-900 hover:bg-gray-50">
                              Superadmin
                            </a>
                          </Link>
                        )}

                        {/* Rest of user dropdown */}
                        {userDropDown.map((item) => (
                          <div key={item.name}>
                            {item.submenu ? (
                              <div className="relative">
                                <button
                                  className="flex items-center justify-between w-full p-2 rounded-lg text-base text-gray-900 hover:bg-gray-50"
                                  onClick={() =>
                                    setShowClassicSubmenu(!showClassicSubmenu)
                                  }
                                >
                                  <div>
                                    {item.name}
                                    <span className="ml-1 text-sm text-gray-500">
                                      {item.description}
                                    </span>
                                  </div>
                                  <FaCaretDown
                                    className={classNames(
                                      showClassicSubmenu ? 'rotate-180' : '',
                                      'h-4 w-4 transition-transform'
                                    )}
                                  />
                                </button>
                                {showClassicSubmenu && (
                                  <div className="ml-4 mt-1 space-y-1">
                                    {item.submenu.map((subItem) => (
                                      <Link
                                        key={subItem.name}
                                        href={subItem.href}
                                      >
                                        <a className="block p-2 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded">
                                          {subItem.name}
                                        </a>
                                      </Link>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <Link href={item.href}>
                                <a
                                  className={classNames(
                                    'block p-2 rounded-lg text-base text-gray-900 hover:bg-gray-50'
                                  )}
                                >
                                  {item.name}
                                  {item.description && (
                                    <span className="ml-1 text-sm text-gray-500">
                                      {item.description}
                                    </span>
                                  )}
                                </a>
                              </Link>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </Popover.Panel>
              </Transition>
            </>
          )}
        </Popover>
      );
    }
    return null;
  }

  const renderDropdownMobile = () => {
    const [showClassicSubmenu, setShowClassicSubmenu] = useState(false);
    const [showTeamAdminSubmenu, setShowTeamAdminSubmenu] = useState(false);

    if (userRole === 'editor' || userRole === 'admin') {
      return (
        <div className="space-y-4">
          {/* Team Admin */}
          {isTeamAdmin && (
            <div>
              <button
                className="flex items-center justify-between w-full text-base font-medium text-gray-900 hover:text-gray-700 py-1"
                onClick={() => setShowTeamAdminSubmenu(!showTeamAdminSubmenu)}
              >
                <span>Team Admin</span>
                <FaCaretDown
                  className={classNames(
                    showTeamAdminSubmenu ? 'rotate-180' : '',
                    'h-4 w-4 transition-transform'
                  )}
                />
              </button>
              {showTeamAdminSubmenu && (
                <div className="ml-4 mt-2 space-y-2">
                  {teamAdminSubmenu.map((subItem) => (
                    <Link key={subItem.name} href={subItem.href}>
                      <a className="block text-sm text-gray-600 hover:text-gray-900 py-1">
                        {subItem.name}
                      </a>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Superadmin */}
          {userRole === 'admin' && (
            <Link href="/superadmin">
              <a className="block text-base font-medium text-gray-900 hover:text-gray-700 py-1">
                Superadmin
              </a>
            </Link>
          )}

          {/* Rest of dropdown */}
          {userDropDown.map((item) => (
            <div key={item.name}>
              {item.submenu ? (
                <div>
                  <button
                    className="flex items-center justify-between w-full text-base font-medium text-gray-900 hover:text-gray-700 py-1"
                    onClick={() => setShowClassicSubmenu(!showClassicSubmenu)}
                  >
                    <div>
                      {item.name}
                      <span className="text-sm text-gray-500 ml-1">
                        {item.description}
                      </span>
                    </div>
                    <FaCaretDown
                      className={classNames(
                        showClassicSubmenu ? 'rotate-180' : '',
                        'h-4 w-4 transition-transform'
                      )}
                    />
                  </button>
                  {showClassicSubmenu && (
                    <div className="ml-4 mt-2 space-y-2">
                      {item.submenu.map((subItem) => (
                        <Link key={subItem.name} href={subItem.href}>
                          <a className="block text-sm text-gray-600 hover:text-gray-900 py-1">
                            {subItem.name}
                          </a>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link href={item.href}>
                  <a
                    className={classNames(
                      'block text-base font-medium text-gray-900 hover:text-gray-700 py-1',
                      item.name === 'Host a Meet-&-Greet' ? 'mb-2' : ''
                    )}
                  >
                    {item.name}
                    {item.description && (
                      <span className="text-sm text-gray-500 ml-1">
                        {item.description}
                      </span>
                    )}
                  </a>
                </Link>
              )}
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  function renderHeaderMobile() {
    if (userRole === 'guest') {
      return (
        <div className="grid gap-y-8">
          {loggedOut.map((item) => (
            <Link key={item.name} href={item.href}>
              <a
                key={item.name}
                className="text-base font-medium text-gray-900 hover:text-gray-700"
              >
                {item.name}
              </a>
            </Link>
          ))}
        </div>
      );
      //Links for logged in users
    } else if (userRole === 'editor' || userRole === 'admin') {
      // These are now shown outside the hamburger menu on mobile
      return null;
    }
    return null;
  }

  return (
    <Popover className="sticky top-0 z-40 bg-white mb-1">
      <div className="px-4 mx-auto max-w-7xl sm:px-6">
        <div className="flex items-center justify-between py-3 md:border-b-2 md:border-gray-100 md:justify-start md:space-x-10">
          <div className="flex justify-start lg:w-0 lg:flex-1">
            <Link href="/">
              <a className="flex items-center">
                <span className="sr-only">Confirmed</span>
                <img
                  className="h-8 w-auto sm:h-10 md:h-12 lg:h-14 xl:h-16 object-contain"
                  src="/confirmedTMLogo.svg"
                  alt="Confirmed Logo"
                />
              </a>
            </Link>
          </div>
          <div className="-my-2 -mr-2 md:hidden">
            <Popover.Button className="inline-flex items-center justify-center gap-2 px-4 py-3 text-gray-700 bg-white rounded-md hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 border border-gray-300">
              <FaBars className="w-5 h-5" />
              <span className="text-sm font-medium">Menu</span>
            </Popover.Button>
          </div>
          <Popover.Group
            as="nav"
            className="items-center justify-end hidden space-x-10 md:flex"
          >
            <div className="items-center justify-end hidden space-x-5 md:flex">
              <div>{renderHeader()}</div>
              <div>{renderDropdown()}</div>
            </div>
          </Popover.Group>
        </div>
      </div>
      {userRole !== 'guest' && (
        <div className="px-4 mx-auto border-b-2 border-gray-100 max-w-7xl sm:px-6 md:hidden ">
          <div className="flex overflow-x-auto py-4 space-x-6 no-scrollbar scroll-smooth">
            {appPages
              .filter((page) => {
                if (page.name === 'Encounters') {
                  return ALLOWED_ENCOUNTERS_EDITIONS.includes(
                    accountDetails?.edition as Edition
                  );
                }
                return true;
              })
              .map((item) => (
                <Link key={item.name} href={item.href}>
                  <a
                    className={classNames(
                      'text-[1.1rem] font-medium hover:text-gray-900 whitespace-nowrap px-1',
                      router.pathname === item.href
                        ? 'text-confirmed-secondary'
                        : 'text-gray-500'
                    )}
                  >
                    {item.name}
                  </a>
                </Link>
              ))}
          </div>
        </div>
      )}

      <Transition
        as={Fragment}
        enter="duration-200 ease-out"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="duration-100 ease-in"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <Popover.Panel
          focus
          className="absolute inset-x-0 top-0 p-2 transition origin-top-right transform md:hidden z-[9999]"
        >
          <div className="bg-white divide-y-2 rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 divide-gray-50">
            <div className="px-5 pt-5">
              <div className="flex items-center justify-between">
                <div>
                  <img
                    className="h-8 w-auto object-contain"
                    src="/confirmedTMLogo.svg"
                    alt="Confirmed Logo"
                  />
                </div>
                <div className="-mr-2">
                  <Popover.Button className="inline-flex items-center justify-center p-2 text-gray-400 bg-white rounded-md hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500">
                    <span className="sr-only">Close menu</span>
                    <FaTimes />
                  </Popover.Button>
                </div>
              </div>
              <div className="mt-6">
                {/* {loggedOut.map((item) => (
                    <a
                      key={item.name}
                      href={item.href}
                      className="flex items-center p-3 -m-3 rounded-md hover:bg-gray-50"
                    >
                      <item.icon className="flex-shrink-0 w-6 h-6 text-indigo-600" aria-hidden="true" />
                      <span className="ml-3 text-base font-medium text-gray-900">
                        {item.name}
                      </span>
                    </a>
                  ))} */}
                <nav>{renderHeaderMobile()}</nav>
              </div>
            </div>
            <div className="px-5 py-6 space-y-6">
              {/* {userDropDown.map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    className="text-base font-medium text-gray-900 hover:text-gray-700"
                  >
                    {item.name}
                  </a>
                ))} */}

              <div>{renderDropdownMobile()}</div>
            </div>
          </div>
        </Popover.Panel>
      </Transition>
    </Popover>
  );
};

export default Header;

export async function getServerSideProps(context: any) {
  const { req } = context;

  const hostName = req.headers.host;

  return { props: { hostName } };
}
